import * as Yup from "yup";
import { useState, useEffect } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useFormik, Form, FormikProvider } from "formik";
import { Icon } from "@iconify/react";
import eyeFill from "@iconify/icons-eva/eye-fill";
import eyeOffFill from "@iconify/icons-eva/eye-off-fill";
// material
import {
  Link,
  Stack,
  Checkbox,
  TextField,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { authService } from "src/services";
import Token from "../../../util/Token";
import enums from "src/util/enums";
import { authSlice } from "src/store/auth/auth.slice";
import { useDispatch } from "react-redux";
import { alertSlice } from "src/store/alert/alert.slice";
import { useMutation } from "@tanstack/react-query";
import { loginFormValidationSchema } from "src/util/validationSchemas";
import { useDomainConfig } from "src/hooks/useDomainConfig";
import { domain } from "process";

export default function LoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const { mutate, isLoading, reset } = useMutation(authService.login);
  const navigate = useNavigate();
  const domainConfig = useDomainConfig();

  const onLoginSuccess = (response) => {
    const loginResponse = response?.data?.data;
    if (loginResponse?.access_token) {
      Token.set(loginResponse.access_token);
      dispatch(authSlice.actions.setAuth(response?.data?.data?.user));
      navigate(enums.path.DASHBOARD_APP);
    } else {
      dispatch(
        alertSlice.actions.setAlert({
          severity: "error",
          open: true,
          message: "Error in login. Please try again.",
        })
      );
    }
  };

  const onLoginError = (error) => {
    dispatch(
      alertSlice.actions.setAlert({
        severity: "error",
        open: true,
        message: error?.response?.data?.message ?? error.message,
      })
    );
  };

  const onSubmit = (loginData, { setSubmitting }) => {
    const { remember, ...loginPayload } = loginData;
    
    // Handle remember me functionality
    if (remember) {
      localStorage.setItem('rememberedUsername', loginData.username);
      localStorage.setItem('rememberMe', 'true');
    } else {
      localStorage.removeItem('rememberedUsername');
      localStorage.removeItem('rememberMe');
    }
    
    mutate(
      {
        ...loginPayload,
        password: window.btoa(loginData.password),
        domain: domainConfig.domain,
      },
      {
        onSuccess: onLoginSuccess,
        onError: onLoginError,
        onSettled: () => reset(),
      }
    );
    setSubmitting(false);
  };

  // Get saved credentials for initial values
  const getInitialValues = () => {
    const savedUsername = localStorage.getItem('rememberedUsername');
    const rememberMe = localStorage.getItem('rememberMe') === 'true';
    
    return {
      username: savedUsername || "",
      password: "",
      remember: rememberMe || true,
    };
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema: loginFormValidationSchema,
    onSubmit: onSubmit,
  });

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps } =
    formik;

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={3} sx={{ pb: 3 }}>
          <TextField
            fullWidth
            autoComplete="username"
            type="email"
            label="Username"
            InputLabelProps={{shrink:true}}
            {...getFieldProps("username")}
            error={Boolean(touched.email && errors.email)}
            helperText={touched.email && errors.email}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '1rem',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.95rem',
                fontWeight: 500,
              },
            }}
          />

          <TextField
            fullWidth
            autoComplete="current-password"
            type={showPassword ? "text" : "password"}
            label="Password"
            InputLabelProps={{shrink:true}}
            {...getFieldProps("password")}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton 
                    onClick={handleShowPassword} 
                    edge="end"
                    sx={{ 
                      color: 'text.secondary',
                      '&:hover': { color: 'primary.main' }
                    }}
                  >
                    <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '1rem',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.95rem',
                fontWeight: 500,
              },
            }}
          />
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ my: 2 }}
        >
          <FormControlLabel
            control={
              <Checkbox
                {...getFieldProps("remember")}
                checked={values.remember}
                sx={{
                  '&.Mui-checked': {
                    color: 'primary.main',
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                Remember me
              </Typography>
            }
          />

          <RouterLink 
            to="/reset"
            style={{
              textDecoration: 'none',
              color: '#1976d2',
              fontWeight: 600,
            }}
            onMouseEnter={(e) => {
              e.target.style.textDecoration = 'underline';
              e.target.style.color = '#1565c0';
            }}
            onMouseLeave={(e) => {
              e.target.style.textDecoration = 'none';
              e.target.style.color = '#1976d2';
            }}
          >
            <Typography variant="subtitle2">
              Forgot password?
            </Typography>
          </RouterLink>
        </Stack>

        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting || isLoading}
          sx={{
            height: 52,
            fontSize: '1.1rem',
            fontWeight: 700,
            letterSpacing: '0.5px',
            textTransform: 'none',
            borderRadius: 3,
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            boxShadow: '0 4px 14px 0 rgba(25, 118, 210, 0.39)',
            mt: 2,
            mb: 2,
            '&:hover': {
              background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
              boxShadow: '0 6px 20px 0 rgba(25, 118, 210, 0.5)',
              transform: 'translateY(-2px)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
            '&.Mui-disabled': {
              background: 'linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%)',
              color: 'text.disabled',
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {isSubmitting || isLoading ? 'Signing In...' : 'Sign In'}
        </LoadingButton>
      </Form>
    </FormikProvider>
  );
}
